import React, { useState, useRef, useMemo } from 'react';
import { X, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { formatCurrency, formatLocalDate } from '../../utils/formatters';
import { DEPOSIT_OFFLINE_METHODS } from '../../utils/quoteSchema';
// creditspread-d1a4e8b6 -- Seam 15: the oldest-first preview computation moved to
// a shared util so Record Payment and the new Spread Credit modal cannot drift.
import { computeSpreadPreview } from '../../utils/creditSpreadPreview';

// acctpay-6c2f8a94 -- Seam 3 (Record a Payment to the Customer Account). Records
// an offline payment straight to a customer's account with NO invoice attached,
// creating an `unappliedPayment` credit via the Seam 2 `createCustomerCredit`
// callable. The money is held on the account; it cannot be applied to an invoice
// yet (that is Seam 4), so this surface deliberately offers no apply affordance.
//
// payoffl-4a1c7e93 -- Seam 11 Part C adds the "Apply to open invoices (oldest first)"
// toggle (default ON). Toggle ON routes to recordOfflineAccountPaymentAndSpread, which
// creates ONE credit and spreads it across the customer's open invoices oldest-first
// (the staff sibling of the portal Pay All). Toggle OFF keeps the original create-only
// path. The toggle defaults ON, so an operator who only means to PARK money must notice
// and untick it -- the guard against a missed untick is the affected-invoices preview,
// rendered directly ABOVE the submit button (never below, never behind a scroll) so a
// list of invoices they did not expect is impossible to miss before they click.

// Mint a client idempotency key. crypto.randomUUID is present on localhost and
// HTTPS in every browser Spatchy targets; the fallback covers only exotic gaps.
const newIdempotencyKey = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `acctpay-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

const RecordAccountPaymentModal = ({ isOpen, onClose, customer, invoices = [], onSuccess }) => {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState(DEPOSIT_OFFLINE_METHODS.CHEQUE);
    const [date, setDate] = useState(formatLocalDate());
    const [memo, setMemo] = useState('');
    // Seam 11 Part C -- default ON: the common case is a cheque covering several invoices.
    const [applyToInvoices, setApplyToInvoices] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(false);
    // The server's ACTUAL outcome (applied count + residual), shown on success in place
    // of the client estimate so the confirmed message is always the truth.
    const [successDetail, setSuccessDetail] = useState(null);

    // acctpay-6c2f8a94 -- idempotency-key lifecycle. The key is minted at SUBMIT
    // time (never at modal-open) and held in a ref for the life of ONE attempt:
    //   - submittingRef is a SYNCHRONOUS double-click guard -- React state lags a
    //     rapid second click, but a ref write is immediate, so (A) rejects it.
    //   - idemKeyRef gets a fresh key only when none is held (B); if the guard is
    //     ever beaten, both calls reuse the SAME key so the server dedupes on its
    //     deterministic creditId (true for BOTH the create-only and spread paths --
    //     the spread callable anchors its creditId on this same key).
    //   - cleared on success (C) -> the next payment mints a new key, so two real
    //     same-amount payments become two credits.
    //   - kept on failure (D) -> a genuine retry dedupes; a CHANGED amount under
    //     the same key trips the server's failed-precondition guard, surfaced
    //     verbatim.
    const submittingRef = useRef(false);
    const idemKeyRef = useRef(null);

    const amountNum = round2(amount);

    // Client-side PREVIEW of the oldest-first spread (shared with the Spread Credit
    // modal via computeSpreadPreview). Best-effort guidance only: the server recomputes
    // each invoice's authoritative balance inside its own transaction at apply time, so
    // this shows what WILL happen, not a promise. The guard keeps the original behaviour
    // (null unless the toggle is on and the amount is positive); the computation itself
    // filters to invoices with a real positive balance (> 0.01), matching the server
    // candidate set exactly (no status filter).
    const preview = useMemo(() => {
        if (!applyToInvoices || !(amountNum > 0)) return null;
        return computeSpreadPreview(invoices, amountNum);
    }, [applyToInvoices, amountNum, invoices]);

    if (!isOpen) return null;

    const resetForm = () => {
        setAmount('');
        setMethod(DEPOSIT_OFFLINE_METHODS.CHEQUE);
        setDate(formatLocalDate());
        setMemo('');
        setApplyToInvoices(true);
        setFormError(null);
        setFormSuccess(false);
        setSuccessDetail(null);
    };

    const handleClose = () => {
        if (submittingRef.current) return; // never close mid-flight
        idemKeyRef.current = null;
        resetForm();
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submittingRef.current) return; // (A) reject a rapid second click

        if (!Number.isFinite(amountNum) || amountNum <= 0) {
            setFormError('Enter an amount greater than zero.');
            return;
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        setFormError(null);
        if (!idemKeyRef.current) idemKeyRef.current = newIdempotencyKey(); // (B)

        try {
            if (applyToInvoices) {
                // Toggle ON: create ONE credit and spread it across open invoices oldest-first.
                const fn = httpsCallable(functions, 'recordOfflineAccountPaymentAndSpread');
                const res = await fn({
                    customerId: customer.id,
                    originalAmount: amountNum,
                    method,
                    memo: memo || null,
                    originatedAt: date,
                    idempotencyKey: idemKeyRef.current,
                });
                const data = res?.data || {};
                setSuccessDetail({
                    appliedCount: Array.isArray(data.applied) ? data.applied.length : 0,
                    remaining: round2(data.remainingCredit),
                });
            } else {
                // Toggle OFF: original Seam 3 behaviour -- create an unapplied account credit.
                const fn = httpsCallable(functions, 'createCustomerCredit');
                await fn({
                    customerId: customer.id,
                    type: 'unappliedPayment',
                    origin: 'manual',
                    originalAmount: amountNum,
                    originatedAt: date,
                    method,
                    memo: memo || null,
                    idempotencyKey: idemKeyRef.current,
                });
                setSuccessDetail(null);
            }
            idemKeyRef.current = null; // (C) success -> next payment is a fresh key
            setFormSuccess(true);
            setTimeout(() => {
                if (onSuccess) onSuccess();
                resetForm();
                onClose();
            }, 1800);
        } catch (error) {
            console.error(error);
            // (D) keep idemKeyRef so a genuine retry dedupes. HttpsError.message
            // (e.g. the Seam 2 conflicting-amount message) reaches the user verbatim.
            setFormError(error?.message || 'Failed to record payment. Please try again.');
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const customerName = customer?.name || 'this customer';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-bold text-gray-800">Record Payment</h2>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <p className="text-sm text-gray-600">
                        Record a payment received from{' '}
                        <span className="font-medium text-gray-800">{customerName}</span>.
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Amount Received</label>
                        <div className="relative mt-1 rounded-md shadow-sm">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="block w-full rounded-md border-gray-300 pl-7 py-2 border focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Payment Date</label>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 border py-2 px-3 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Method</label>
                            <select
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 border py-2 px-3 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                                <option value={DEPOSIT_OFFLINE_METHODS.ETRANSFER}>E-transfer</option>
                                <option value={DEPOSIT_OFFLINE_METHODS.CHEQUE}>Cheque</option>
                                <option value={DEPOSIT_OFFLINE_METHODS.CASH}>Cash</option>
                                <option value={DEPOSIT_OFFLINE_METHODS.OTHER}>Other</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Reference / memo</label>
                        <input
                            type="text"
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                            placeholder="e.g. Chq #4021"
                            className="mt-1 block w-full rounded-md border-gray-300 border py-2 px-3 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                    </div>

                    {/* Seam 11 Part C -- apply toggle. Default ON (the common case). */}
                    <label className="flex items-start gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={applyToInvoices}
                            onChange={(e) => setApplyToInvoices(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">
                            Apply to open invoices (oldest first)
                            <span className="block text-xs text-gray-500">
                                Uncheck to hold the money on the account without applying it.
                            </span>
                        </span>
                    </label>

                    {/* Affected-invoices preview. Positioned HERE, directly above the submit
                        button, so an operator about to park money cannot miss the invoices this
                        will clear. Estimate only; the server is authoritative. */}
                    {preview && (
                        <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm">
                            {preview.rows.length === 0 ? (
                                <p className="text-gray-700">
                                    No open invoices — the full{' '}
                                    <span className="font-semibold">{formatCurrency(amountNum)}</span>{' '}
                                    will be held as account credit.
                                </p>
                            ) : (
                                <>
                                    <p className="font-medium text-purple-900 mb-2">
                                        This will be applied to {preview.rows.length} invoice{preview.rows.length === 1 ? '' : 's'}:
                                    </p>
                                    <ul className="space-y-1 max-h-48 overflow-y-auto pr-1">
                                        {preview.rows.map((r) => (
                                            <li key={r.id} className="flex justify-between items-center gap-2">
                                                <span className="text-gray-700">Invoice #{r.number}</span>
                                                <span className="tabular-nums text-gray-800">
                                                    {formatCurrency(r.apply)}
                                                    <span className={`ml-2 text-xs ${r.clears ? 'text-green-600' : 'text-amber-600'}`}>
                                                        {r.clears ? 'clears' : 'partial'}
                                                    </span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-2 pt-2 border-t border-purple-200 text-gray-700">
                                        <span className="font-semibold">{formatCurrency(preview.totalApplied)}</span>{' '}
                                        across {preview.rows.length} invoice{preview.rows.length === 1 ? '' : 's'}
                                        {preview.leftover > 0.01 && (
                                            <>; <span className="font-semibold">{formatCurrency(preview.leftover)}</span> left as account credit</>
                                        )}
                                        <span className="block text-xs text-gray-500 mt-0.5">Estimate — final amounts are confirmed when you record.</span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isSubmitting || formSuccess}
                        className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 flex justify-center items-center gap-2"
                    >
                        {isSubmitting ? <Loader2 className="animate-spin" /> : 'Record Payment'}
                    </button>

                    {formSuccess && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                            <CheckCircle size={16} className="shrink-0" />
                            {successDetail
                                ? `Payment recorded. ${successDetail.appliedCount} invoice${successDetail.appliedCount === 1 ? '' : 's'} updated${successDetail.remaining > 0.01 ? `; ${formatCurrency(successDetail.remaining)} left as account credit` : ''}.`
                                : `Payment of ${formatCurrency(amountNum)} recorded to ${customerName}'s account.`}
                        </div>
                    )}

                    {formError && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            <AlertTriangle size={16} className="shrink-0" />
                            {formError}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default RecordAccountPaymentModal;
